import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Save, Clock, MapPin } from 'lucide-react';
import { DealerLocation, LocationHours, UpdateLocationHoursRequest } from '../types';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const LocationInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  
  .location-name {
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .location-details {
    font-size: 0.875rem;
    color: #0369a1;
  }
`;

const HoursGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DayRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 120px 120px;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  
  &:hover {
    background: #f9fafb;
  }
`;

const DayLabel = styled.div`
  font-weight: 600;
  color: #374151;
`;

const Toggle = styled.div<{ enabled: boolean }>`
  width: 48px;
  height: 24px;
  background: ${props => props.enabled ? '#ed2024' : '#d1d5db'};
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.enabled ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const TimeInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #ed2024;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: ${props => props.variant === 'primary' ? 'none' : '1px solid #d1d5db'};
  background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    ${props => props.variant === 'primary' ? `
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    ` : `
      background: #f3f4f6;
      border-color: #9ca3af;
    `}
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DAYS = [
  'Sunday',
  'Monday', 
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

const LocationHoursPage: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [location, setLocation] = useState<DealerLocation | null>(null);
  const [hours, setHours] = useState<LocationHours[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (locationId) {
      loadLocationData();
    }
  }, [locationId]);

  const loadLocationData = async () => {
    if (!locationId || !user) return;

    try {
      setIsLoading(true);
      
      let foundLocation: DealerLocation | undefined;
      
      if (user.userType === 'dealer') {
        // For dealer users, get their dealer's locations
        const response = await apiService.getDealerLocations(user.dealerId);
        if (response.success && response.data) {
          foundLocation = response.data.find(loc => loc.id === locationId);
          
          // Check if staff user has access to this location
          if (foundLocation && (user as any).role === 'dealer_staff') {
            const userLocationIds = (user as any).locationIds || [];
            if (!userLocationIds.includes(locationId)) {
              toast.error('You do not have access to this location');
              navigate('/locations');
              return;
            }
          }
        }
      } else {
        // For admin users, we need to find the location across all dealers
        // We'll need to add a new API endpoint for this, but for now let's try to get it from sessionStorage
        const dealerId = sessionStorage.getItem('currentDealerId');
        if (dealerId) {
          const response = await apiService.getDealerLocations(dealerId);
          if (response.success && response.data) {
            foundLocation = response.data.find(loc => loc.id === locationId);
          }
        }
        
        // If we still don't have the location, we need a different approach
        if (!foundLocation) {
          // Let's try to get all dealers and find the location (not efficient, but works for now)
          const dealersResponse = await apiService.getDealers();
          if (dealersResponse.success && dealersResponse.data) {
            for (const dealer of dealersResponse.data) {
              const locationsResponse = await apiService.getDealerLocations(dealer.id);
              if (locationsResponse.success && locationsResponse.data) {
                const location = locationsResponse.data.find(loc => loc.id === locationId);
                if (location) {
                  foundLocation = location;
                  // Store dealer ID for back navigation
                  sessionStorage.setItem('currentDealerId', dealer.id);
                  break;
                }
              }
            }
          }
        }
      }

      if (!foundLocation) {
        toast.error('Location not found');
        navigate('/dealers');
        return;
      }

      setLocation(foundLocation);
      setHours(foundLocation.hours || []);
    } catch (error) {
      console.error('Error loading location data:', error);
      toast.error('Failed to load location data');
      navigate('/dealers');
    } finally {
      setIsLoading(false);
    }
  };

  const updateDayHours = (dayOfWeek: number, field: string, value: any) => {
    setHours(prevHours => {
      const existingHour = prevHours.find(h => h.dayOfWeek === dayOfWeek);
      
      if (existingHour) {
        return prevHours.map(h => 
          h.dayOfWeek === dayOfWeek 
            ? { ...h, [field]: value }
            : h
        );
      } else {
        const newHour: LocationHours = {
          id: `temp-${dayOfWeek}`,
          locationId: locationId!,
          dayOfWeek,
          isOpen: field === 'isOpen' ? value : true,
          openTime: field === 'openTime' ? value : '09:00',
          closeTime: field === 'closeTime' ? value : '17:00',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        return [...prevHours, newHour];
      }
    });
  };

  const getDayHours = (dayOfWeek: number): LocationHours => {
    const dayHours = hours.find(h => h.dayOfWeek === dayOfWeek);
    return dayHours || {
      id: `default-${dayOfWeek}`,
      locationId: locationId!,
      dayOfWeek,
      isOpen: false,
      openTime: '09:00',
      closeTime: '17:00',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  };

  const handleSave = async () => {
    if (!locationId) return;

    try {
      setIsSaving(true);
      
      const hoursData: UpdateLocationHoursRequest = {
        locationId,
        hours: DAYS.map((_, index) => {
          const dayHours = getDayHours(index);
          return {
            dayOfWeek: index,
            isOpen: dayHours.isOpen,
            openTime: dayHours.isOpen ? dayHours.openTime : undefined,
            closeTime: dayHours.isOpen ? dayHours.closeTime : undefined
          };
        })
      };

      const response = await apiService.updateLocationHours(locationId, hoursData);
      if (response.success) {
        toast.success('Hours updated successfully');
      } else {
        toast.error('Failed to update hours');
      }
    } catch (error) {
      console.error('Error updating hours:', error);
      toast.error('Failed to update hours');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (location && user?.userType === 'dealer') {
      navigate('/dealer-dashboard');
    } else if (location) {
      const dealerId = sessionStorage.getItem('currentDealerId') || location.dealerId;
      navigate(`/dealers/${dealerId}/edit`);
    } else {
      navigate('/dealers');
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
            Loading location hours...
          </div>
        </Card>
      </PageContainer>
    );
  }

  if (!location) {
    return (
      <PageContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
            Location not found
          </div>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack}>
          <ArrowLeft />
          Back
        </BackButton>
        <h1>
          <Clock />
          Location Hours
        </h1>
      </PageHeader>

      <Card>
        <LocationInfo>
          <div className="location-name">
            <MapPin size={16} />
            {location.name}
          </div>
          <div className="location-details">
            {location.address && `${location.address}, `}
            {location.city && `${location.city}, `}
            {location.state} {location.zipCode}
          </div>
        </LocationInfo>

        <HoursGrid>
          {DAYS.map((day, index) => {
            const dayHours = getDayHours(index);
            return (
              <DayRow key={day}>
                <DayLabel>{day}</DayLabel>
                <Toggle
                  enabled={dayHours.isOpen}
                  onClick={() => updateDayHours(index, 'isOpen', !dayHours.isOpen)}
                />
                <TimeInput
                  type="time"
                  value={dayHours.openTime || '09:00'}
                  onChange={(e) => updateDayHours(index, 'openTime', e.target.value)}
                  disabled={!dayHours.isOpen}
                />
                <TimeInput
                  type="time"
                  value={dayHours.closeTime || '17:00'}
                  onChange={(e) => updateDayHours(index, 'closeTime', e.target.value)}
                  disabled={!dayHours.isOpen}
                />
              </DayRow>
            );
          })}
        </HoursGrid>

        <ButtonGroup>
          <Button type="button" onClick={handleBack}>
            Cancel
          </Button>
          <Button 
            type="button" 
            variant="primary" 
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save />
            {isSaving ? 'Saving...' : 'Save Hours'}
          </Button>
        </ButtonGroup>
      </Card>
    </PageContainer>
  );
};

export default LocationHoursPage;
